import { useCallback, useState } from 'react'
import { keepPreviousData, QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { Loader2, ShieldAlert, WifiOff } from '@/lib/icons'
import { AuthErrorCode } from '@ayub/shared'
import type { DashboardResponse } from '@ayub/shared'
import { AppShell } from '@/components/AppShell'
import { AuthErrorScreen } from '@/components/AuthErrorScreen'
import { LoginPage } from '@/components/auth/LoginPage'
import { useAuthSession } from '@/components/auth/hooks'
import { Button } from '@/components/ui/button'
import { Dashboard } from '@/components/Dashboard'
import { ApiError, apiGet } from '@/lib/api'
import { isInsideTelegram } from '@/lib/telegram'

/**
 * Xato turiga qarab qayta urinish strategiyasi:
 * - Autentifikatsiya xatolari (`code` bor) — qayta urinish yordam bermaydi, foydalanuvchi
 *   harakati kerak (botga qaytish). Darhol to'xtaymiz.
 * - Boshqa API xatolari (5xx, validatsiya) — bir necha marta, tez orada tugaydi.
 * - Tarmoq xatolari (fetch umuman bajarilmadi — offline, DNS va h.k.) — qattiqroq qayta
 *   urinamiz, chunki bu ko'pincha vaqtinchalik uzilish.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.code) return false
    return failureCount < 2
  }
  return failureCount < 5
}

function DashboardPage() {
  const insideTelegram = isInsideTelegram()

  // Dashboard tez-tez qayta ochiladi (admin kun davomida Telegram'ga qaytib-qaytib keladi),
  // shuning uchun: fokusga qaytganda avtomatik yangilanadi, 30s ichida "stale" hisoblanmaydi
  // (ortiqcha so'rov yubormaslik uchun) va fonda 5 daqiqada bir yangilanadi. `keepPreviousData`
  // fon yangilanishi paytida eski ma'lumotni saqlab turadi — ekran hech qachon bo'shab qolmaydi.
  const query = useQuery<DashboardResponse, Error>({
    queryKey: ['dashboard'],
    queryFn: () => apiGet<DashboardResponse>('/dashboard'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    retry: shouldRetry,
    placeholderData: keepPreviousData,
  })

  const handleRefresh = useCallback(() => {
    void query.refetch()
  }, [query])

  const dashboardError = query.error
  if (dashboardError instanceof ApiError && dashboardError.code) {
    // `AuthErrorScreen` FAQAT Telegram rejimida ishlatiladi — u Telegram'ga xos harakatlar
    // taklif qiladi ("Yopish" tugmasi `closeMiniApp()`, matni "botdan qayta oching" deydi),
    // brauzerda bularning ma'nosi yo'q. Brauzer rejimida 401 (AUTH_MISSING/INVALID/EXPIRED)
    // allaqachon `lib/api.ts`dagi markaziy joyda ushlanib sahifa qayta yuklanmoqda bo'ladi —
    // 403 (NOT_ADMIN) esa doimiy holat, alohida (Telegram'ga ishora qilmaydigan) ekran kerak.
    return insideTelegram ? (
      <AuthErrorScreen
        code={dashboardError.code}
        insideTelegram={insideTelegram}
        onRetry={handleRefresh}
      />
    ) : (
      <BrowserAuthErrorScreen code={dashboardError.code} />
    )
  }

  return (
    <AppShell>
      <Dashboard
        data={query.data}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        error={query.isError ? dashboardError : null}
        onRefresh={handleRefresh}
      />
    </AppShell>
  )
}

/**
 * Brauzer rejimida (`isInsideTelegram()` `false`) render qilinadigan ildiz.
 *
 * Telegram ichida bo'lsa `App()` bu komponentga UMUMAN kirmaydi — `initData` bilan har bir
 * so'rov o'zi autentifikatsiya bo'lgani uchun oldindan sessiya tekshiruvi shart emas, HOZIRGI
 * oqim (`DashboardPage` to'g'ridan-to'g'ri) o'zgarishsiz qoladi. Brauzerda esa `initData` yo'q
 * — bu XATO EMAS, shuning uchun avval `GET /api/auth/me` orqali sessiya (cookie) tekshiriladi:
 * 401 kelsa login sahifasi, 200 kelsa xuddi shu `DashboardPage`.
 */
function BrowserGate() {
  const session = useAuthSession()

  if (session.status === 'unauthenticated') {
    return <LoginPage onSuccess={session.refetch} />
  }

  if (session.status === 'error') {
    return <SessionCheckErrorScreen onRetry={session.refetch} />
  }

  if (session.status === 'loading') {
    return <FullScreenSpinner />
  }

  return <DashboardPage />
}

/**
 * Brauzer rejimidagi AUTH_* xato ekrani — `AuthErrorScreen`ning o'rnini bosadi (u faqat
 * Telegram uchun, yuqoridagi izohga qarang).
 *
 * 401 (MISSING/INVALID/EXPIRED): bu holatda `lib/api.ts` markaziy joyi allaqachon
 * `window.location.reload()`ni boshlab yuborgan (`isInsideTelegram()` `false` bo'lganda) —
 * shu qayta yuklanish tugagunicha oraliq holat ko'rsatiladi, alohida "qayta urinish" tugmasi
 * KERAK EMAS (u yana 401 qaytarib, yana reload boshlagan bo'lardi — foydasiz).
 *
 * 403 (NOT_ADMIN): doimiy holat, qayta yuklash yordam bermaydi — botga qaytish taklifisiz
 * (brauzerda ma'nosiz) oddiy "ruxsat yo'q" xabari.
 */
function BrowserAuthErrorScreen({ code }: { code: AuthErrorCode }) {
  if (code === AuthErrorCode.NOT_ADMIN) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-app-card text-app-hint">
          <ShieldAlert size={28} aria-hidden="true" />
        </div>
        <h1 className="text-lg font-semibold">Sizda ruxsat yo&apos;q</h1>
        <p className="max-w-sm text-sm text-app-hint">
          Bu panel faqat administratorlar uchun ochiq. Agar bu xato deb hisoblasangiz, bosh
          administratorga murojaat qiling.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <Loader2 size={28} className="animate-spin text-app-hint" aria-hidden="true" />
      <p className="text-sm text-app-hint">Sessiya tugadi, sahifa yangilanmoqda...</p>
    </div>
  )
}

/** `GET /api/auth/me` javob bermadi (tarmoq/5xx) — bu AUTH_* kodi emas, oddiy retry ekrani. */
function SessionCheckErrorScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-app-card text-app-hint">
        <WifiOff size={28} aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold">Sessiyani tekshirib bo'lmadi</h1>
      <p className="max-w-sm text-sm text-app-hint">
        Server bilan aloqa yo'q. Qayta urinib ko'ring.
      </p>
      <Button className="mt-2" onClick={onRetry}>
        Qayta urinish
      </Button>
    </div>
  )
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Loader2 size={28} className="animate-spin text-app-hint" aria-hidden="true" />
    </div>
  )
}

export function App() {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <QueryClientProvider client={queryClient}>
      {isInsideTelegram() ? <DashboardPage /> : <BrowserGate />}
    </QueryClientProvider>
  )
}
